import is from '@sindresorhus/is';
import type { QueryKey, UseQueryResult } from '@tanstack/react-query';
import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import type Result from 'true-myth/result';

import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

const createJobItemVersionsQueryOptions = ({
    enabled,
    fetchVersions,
    itemId,
    queryKey,
}: {
    readonly enabled: boolean | undefined;
    readonly fetchVersions: () => Promise<JobProfileVersions>;
    readonly itemId: string;
    readonly queryKey: QueryKey;
}) =>
    queryOptions({
        enabled,
        placeholderData: (previousData) => {
            if (is.nullOrUndefined(previousData)) {
                return undefined;
            }

            return previousData.some((version) => version.itemId === itemId) ? previousData : undefined;
        },
        queryFn: fetchVersions,
        queryKey,
        staleTime: 60_000,
        throwOnError: false,
    });

export const useJobItemVersions = ({
    architectureId,
    enabled,
    itemId,
    itemType,
}: {
    readonly architectureId: string;
    readonly enabled?: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
}): UseQueryResult<JobProfileVersions, Error> => {
    const { fetchJobLevelVersions, fetchJobRoleVersions } = useApplicationFetchers();

    const getQueryKey = (): QueryKey => {
        if (itemType === 'job-role') {
            return jobArchitectureKeys.roleVersions(architectureId, itemId);
        }
        if (itemType === 'job-level') {
            return jobArchitectureKeys.levelVersions(architectureId, itemId);
        }

        throw new Error(`Invalid item type: ${itemType}`);
    };

    return useQuery(
        createJobItemVersionsQueryOptions({
            enabled,
            itemId,
            queryKey: getQueryKey(),
            fetchVersions: async () => {
                const result =
                    itemType === 'job-role'
                        ? await fetchJobRoleVersions({ jobArchitectureId: architectureId, jobRoleId: itemId })
                        : await fetchJobLevelVersions({ jobArchitectureId: architectureId, jobLevelId: itemId });

                if (result.isErr === true) {
                    throw result.error;
                }

                return result.value;
            },
        }),
    );
};

const useJobItemVersionsQueries = ({
    architectureId,
    fetchVersions,
    getQueryKey,
    itemIds,
}: {
    readonly architectureId: string;
    readonly fetchVersions: (itemId: string) => Promise<Result<JobProfileVersions, HttpFetcherError>>;
    readonly getQueryKey: (architectureId: string, itemId: string) => QueryKey;
    readonly itemIds: readonly string[];
}): readonly UseQueryResult<JobProfileVersions, Error>[] =>
    useQueries({
        queries: itemIds.map((itemId) =>
            createJobItemVersionsQueryOptions({
                enabled: true,
                itemId,
                queryKey: getQueryKey(architectureId, itemId),
                fetchVersions: async () => {
                    const result = await fetchVersions(itemId);

                    if (result.isErr === true) {
                        throw result.error;
                    }

                    return result.value;
                },
            }),
        ),
    });

export const useJobLevelVersionsQueries = ({
    architectureId,
    levelIds,
}: {
    readonly architectureId: string;
    readonly levelIds: readonly string[];
}): readonly UseQueryResult<JobProfileVersions, Error>[] => {
    const { fetchJobLevelVersions } = useApplicationFetchers();

    return useJobItemVersionsQueries({
        architectureId,
        fetchVersions: (jobLevelId) => fetchJobLevelVersions({ jobArchitectureId: architectureId, jobLevelId }),
        getQueryKey: jobArchitectureKeys.levelVersions,
        itemIds: levelIds,
    });
};

export const useJobRoleVersionsQueries = ({
    architectureId,
    roleIds,
}: {
    readonly architectureId: string;
    readonly roleIds: readonly string[];
}): readonly UseQueryResult<JobProfileVersions, Error>[] => {
    const { fetchJobRoleVersions } = useApplicationFetchers();

    return useJobItemVersionsQueries({
        architectureId,
        fetchVersions: (jobRoleId) => fetchJobRoleVersions({ jobArchitectureId: architectureId, jobRoleId }),
        getQueryKey: jobArchitectureKeys.roleVersions,
        itemIds: roleIds,
    });
};
